import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import apiClient from './api/client';
import { useCurrentDomain } from "./context/DomainContext";
import { Download, RefreshCw, Eye, Copy, ChevronUp, ChevronDown, Search } from 'lucide-react';
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import subDays from "date-fns/subDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import addDays from "date-fns/addDays";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";
import { THEME } from './theme/themeColors';

const QueryAnalyzerLogs = () => {
  const { domain } = useCurrentDomain();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [globalSearch, setGlobalSearch] = useState('');
  const [queryType, setQueryType] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const observerTarget = useRef(null);

  const predefinedBottomRanges = [
    { label: "Today", value: [new Date(), new Date()] },
    { label: "Yesterday", value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
    { label: "This week", value: [startOfWeek(new Date()), endOfWeek(new Date())] },
    { label: "Last 7 days", value: [subDays(new Date(), 6), new Date()] },
    { label: "Last 30 days", value: [subDays(new Date(), 29), new Date()] },
    { label: "This month", value: [startOfMonth(new Date()), endOfMonth(new Date())] },
    { label: "Last month", value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))] },
    { label: "This year", value: [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)] },
    { label: "Last year", value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date(new Date().getFullYear(), 0, 0)] },
    { label: "All time", value: [new Date(new Date().getFullYear() - 5, 0, 1), new Date()] },
  ];

  const fetchLogs = useCallback(async (page = 1, isNewSearch = false) => {
    const setLoader = isNewSearch ? setSearchLoading : setLoading;
    setLoader(true);

    try {
      const params = {
        page: page,
        pageSize: isNewSearch ? 200 : 100,
        FromDate: dateRange.from || undefined,
        ToDate: dateRange.to || undefined,
        QueryType: queryType !== 'all' ? queryType : undefined,
      };

      const res = await apiClient.get('/api/logs/querylog', { params });

      const newLogs = res.data.logs || [];
      
      if (isNewSearch) {
        setLogs(newLogs);
        setCurrentPage(1);
      } else {
        setLogs(prev => [...prev, ...newLogs]);
        setCurrentPage(page);
      }

      setHasMore(!!res.data.hasMore);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load logs');
      console.error(err);
    } finally {
      setLoader(false);
    }
  }, [globalSearch, dateRange, queryType]);

  useEffect(() => {
    setLogs([]);
    setCurrentPage(1);
    setHasMore(false);
    fetchLogs(1, true);
  }, [domain, fetchLogs]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const extractQuery = (activity) => {
    if (!activity) return '';
    const match = activity.match(/Query:\s*(.+)$/is);
    return match ? match[1].trim() : activity;
  };

  const formatSQL = (sql) => {
    if (!sql) return 'No query available';
    let formatted = sql
      .replace(/\[SUCCESS\].*?Query:\s*/i, '')
      .replace(/\[FAILED\].*?Query:\s*/i, '')
      .trim();

    formatted = formatted
      .replace(/\b(SELECT|FROM|WHERE|AND|OR|JOIN|INNER|LEFT|RIGHT|ON|ORDER BY|GROUP BY|HAVING|WITH|NOLOCK)\b/gi, '\n$1')
      .replace(/\s*,\s*/g, ', ')
      .replace(/\s*\(\s*/g, ' (')
      .replace(/\s*\)\s*/g, ') ')
      .trim();

    return formatted;
  };

  const truncatedActivity = (activity) => {
    if (!activity) return '';
    return activity.length > 180 ? activity.substring(0, 180) + '...' : activity;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !searchLoading) {
          fetchLogs(currentPage + 1);
        }
      },
      { threshold: 0.5, rootMargin: '100px' }
    );

    if (observerTarget.current) observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [hasMore, loading, searchLoading, currentPage, fetchLogs]);

  const handleSearch = () => {
    setLogs([]);
    setCurrentPage(1);
    setHasMore(false);
    fetchLogs(1, true);
  };

  const handleReset = () => {
    setGlobalSearch('');
    setDateRange({ from: '', to: '' });
    setQueryType('all');
    setLogs([]);
    setCurrentPage(1);
    setHasMore(false);
    fetchLogs(1, true);
  };

  const filteredLogs = useMemo(() => {
    let result = logs;

    const term = globalSearch.trim().toLowerCase();
    if (term) {
      result = result.filter(log => {
        const searchableText = [
          log.activity || '',
          log.domain || '',
          log.userName || '',
          log.source || '',
          log.userId || '',
          log.timestamp ? log.timestamp.toString() : ''
        ].join(' ').toLowerCase();

        return searchableText.includes(term);
      });
    }

    return result;
  }, [logs, globalSearch]);

  const exportAllToExcel = async () => {
    const exportLoading = toast.loading("Preparing export...");

    try {
      let logsToExport = [];

      if (globalSearch.trim() || queryType !== 'all') {
        logsToExport = filteredLogs;
      } 
      else {
        const allLogs = [];
        let page = 1;
        let hasMoreData = true;

        while (hasMoreData) {
          const res = await apiClient.get('/api/logs/querylog', {
            params: {
              page: page,
              pageSize: 200,
              FromDate: dateRange.from || undefined,
              ToDate: dateRange.to || undefined,
            }
          });

          const batch = res.data.logs || [];
          allLogs.push(...batch);

          hasMoreData = !!res.data.hasMore;
          page++;
        }

        logsToExport = allLogs;
      }

      if (logsToExport.length === 0) {
        toast.error("No data to export");
        return;
      }

      const exportData = logsToExport.map(log => ({
        Timestamp: log.timestamp ? new Date(log.timestamp).toLocaleString() : '',
        Domain: log.domain || '',
        UserName: log.userName || '',
        Source: log.source || '',
        Activity: log.activity || '',
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "QueryLogs");
      XLSX.writeFile(wb, `Query_Analyzer_Logs_${new Date().toISOString().slice(0,10)}.xlsx`);

      toast.success(`Exported ${logsToExport.length} records successfully`);
    } catch (err) {
      toast.error("Failed to export logs");
      console.error(err);
    } finally {
      toast.dismiss(exportLoading);
    }
  };

  const copyToClipboard = async () => {
    if (!selectedActivity) return;
    try {
      await navigator.clipboard.writeText(selectedActivity);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  return (
    <>
      <Toaster position="top-right" />

      <style>{`
        /* Your existing styles remain the same */
        .tbl-compact thead th { background: #f8f9fa; font-size: 11.5px; font-weight: 600; color: #495057; text-transform: uppercase; letter-spacing: 0.3px; white-space: nowrap; border-bottom: 2px solid #dee2e6 !important; padding: 10px 8px !important; }
        .tbl-compact td { padding: 9px 8px !important; vertical-align: middle; border: none !important; }
        .tbl-compact tbody tr { height: 46px; border-bottom: 1px solid #f1f1f1 !important; }
        .tbl-compact tbody tr:last-child { border-bottom: none !important; }
        .tbl-compact tbody tr:hover { background-color: rgba(var(--theme-primary-rgb), 0.06); }
        .form-control-xs { height: 32px !important; font-size: 12px !important; padding: 4px 8px !important; }
        .filter-label { font-size: 11px; font-weight: 600; color: #6c757d; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; }
        .activity-cell { white-space: pre-wrap; line-height: 1.4; max-height: 46px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; position: relative; }
        .eye-btn { position: absolute; right: 20px; top: 50%; transform: translateY(-50%); background: rgba(255,255,255,0.95); border: 1px solid #ddd; border-radius: 4px; padding: 3px 7px; cursor: pointer; color: var(--theme-primary-hover); opacity: 0.85; display: block; }
        .eye-btn:hover { background: var(--theme-primary); color: var(--theme-on-primary); opacity: 1; }
        @media (max-width: 768px) {
          .rsuite .rs-picker-date-range { width: 100% !important; }      
          .rs-picker-date-range .rs-picker-toggle { width: 100% !important; padding: 8px 12px !important; font-size: 13px !important; }      
          .rs-picker-date-range .rs-picker-menu { width: 100% !important; max-width: 100vw !important; left: 0 !important; right: 0 !important; margin: 4px 0 !important; }      
          .rs-picker-date-range .rs-picker-daterange-calendar-group > div:last-child { display: none !important; }
        }
      `}</style>

      <div className="min-vh-100 bg-white">
        <div className="card shadow-sm border-0">
          <div className="card-body py-3 px-3 border-bottom">
            {showFilters && (
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-3">
                <label className="filter-label">Search</label>
                <input
                  type="text"
                  className="form-control form-control-xs"
                  placeholder="Search logs..."
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>

              <div className="col-12 col-md-3">
                <label className="filter-label">Date Range</label>
                <DateRangePicker
                  value={[dateRange.from ? new Date(dateRange.from) : null, dateRange.to ? new Date(dateRange.to) : null]}
                  onChange={(value) => {
                    if (!value || value.length !== 2) {
                      setDateRange({ from: '', to: '' });
                      return;
                    }
                    const [start, end] = value;
                    setDateRange({
                      from: start ? start.toISOString().split('T')[0] : '',
                      to: end ? end.toISOString().split('T')[0] : ''
                    });
                  }}
                  ranges={predefinedBottomRanges}
                  placeholder="Select Date Range"
                  style={{ width: '100%' }}
                  format="MM/dd/yyyy"
                  size="sm"
                  cleanable
                  placement="bottomStart"
                  preventOverflow
                  showOneCalendar={isMobile}
                />
              </div>

              <div className="col-12 col-md-3">
                <label className="filter-label">Query Type</label>
                <div className="d-flex gap-3 mt-1 flex-wrap" style={{ fontSize: '12px' }}>
                  {['all', 'select', 'other'].map(type => (
                    <label key={type} style={{ color: THEME.primaryHover, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input type="radio" name="queryType" value={type} checked={queryType === type} onChange={(e) => setQueryType(e.target.value)} style={{ accentColor: THEME.primary }} /> 
                      {type === 'all' ? 'All Query' : type === 'select' ? 'SELECT Query(s)' : 'Others Query(s)'}
                    </label>
                  ))}
                </div>
              </div>

              <div className="col-12 col-md-auto d-flex gap-2 align-items-end flex-wrap">
                <button 
                  className="btn btn-outline-secondary btn-sm" 
                  style={{ 
                    height: '32px', 
                    width: isMobile ? '36px' : 'auto',
                    padding: isMobile ? '0' : undefined,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }} 
                  onClick={handleReset} 
                  disabled={searchLoading || loading}
                  title="Reset"
                >
                  {isMobile ? <RefreshCw size={16} /> : 'Reset'}
                </button>

                <button 
                  className="btn btn-sm fw-semibold" 
                  style={{ 
                    backgroundColor: THEME.primary, 
                    color: THEME.onPrimary,
                    border: 'none', 
                    height: '32px', 
                    width: isMobile ? '36px' : 'auto',
                    padding: isMobile ? '0' : undefined,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }} 
                  onClick={handleSearch} 
                  disabled={searchLoading || loading}
                  title="Search"
                >
                  {searchLoading ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : isMobile ? (
                    <Search size={16} />
                  ) : (
                    'Search'
                  )}
                </button>

                <button 
                  className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 text-nowrap" 
                  style={{ 
                    height: '32px', 
                    width: isMobile ? '36px' : 'auto',
                    padding: isMobile ? '0' : undefined,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }} 
                  onClick={exportAllToExcel} 
                  disabled={logs.length === 0}
                  title="Export Excel"
                >
                  {isMobile ? <Download size={16} /> : <><Download size={14} /> Export Excel</>}
                </button>

                {/* Hide Filters - Mobile only */}
                <button
                  className="btn btn-sm btn-outline-secondary d-md-none d-flex align-items-center gap-1"
                  style={{ height: '32px', fontSize: '12px' }}
                  onClick={() => setShowFilters(false)}
                >
                  Hide Filters <ChevronUp size={14} />
                </button>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end align-items-center mt-2 pt-2 border-top">
            {!showFilters && (
              <button
                className="btn btn-sm btn-outline-secondary d-md-none d-flex align-items-center gap-1"
                style={{ fontSize: '12px', height: '28px' }}
                onClick={() => setShowFilters(true)}
              >
                Show Filters <ChevronDown size={14} />
              </button>
            )}
          </div>
          </div>

          <div className="table-responsive" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            <table className="table table-hover mb-0 tbl-compact d-none d-md-table" style={{ tableLayout: 'fixed', fontSize: '12px' }}>
              <thead className="sticky-top">
                <tr>
                  <th style={{ width: '12%' }}>TIMESTAMP</th>
                  <th style={{ width: '11%' }}>DOMAIN</th>
                  <th style={{ width: '12%' }}>USER</th>
                  <th style={{ width: '7%' }}>SOURCE</th>
                  <th style={{ width: '60%' }}>ACTIVITY</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 && !loading && !searchLoading ? (
                  <tr><td colSpan="5" className="text-center py-5 text-muted">No logs found.</td></tr>
                ) : filteredLogs.length === 0 && (loading || searchLoading) ? (
                  <tr><td colSpan="5" className="text-center py-5"><span className="ms-2 text-muted">Loading logs...</span></td></tr>
                ) : (
                  filteredLogs.map((log, idx) => (
                    <tr key={idx}>
                      <td>{log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}</td>
                      <td>{log.domain || '—'}</td>
                      <td>{log.userName || '—'}</td>
                      <td><small>{log.source || '—'}</small></td>
                      <td className="activity-cell position-relative">
                        {truncatedActivity(log.activity) || '—'}
                        {log.activity && (
                          <button className="eye-btn" title="View Query" onClick={() => setSelectedActivity(extractQuery(log.activity))}>
                            <Eye size={12} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Card View */}
            <div className="d-md-none" style={{ padding: '0 8px' }}>
              {loading || searchLoading ? (
                <div className="text-center py-5">
                  <span className="spinner-border spinner-border-sm" style={{ color: THEME.primary }} />
                  <div className="mt-2 text-muted">Loading logs...</div>
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className="text-center py-5 text-muted">No logs found.</div>
              ) : (
                filteredLogs.map((log, idx) => (
                  <div key={idx} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                      </div>
                      <button 
                        className="btn btn-sm p-1" 
                        style={{ color: THEME.primaryHover }}
                        onClick={() => setSelectedActivity(extractQuery(log.activity))}
                      >
                        <Eye size={16} />
                      </button>
                    </div>

                    <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                      {log.userName || '—'} <span style={{ color: '#94a3b8', fontSize: '12px' }}>• {log.domain || '—'}</span>
                    </div>

                    <div style={{ fontSize: '12.5px', color: '#334155', marginBottom: '8px' }}>
                      <strong>Source:</strong> {log.source || '—'}
                    </div>

                    <div style={{ fontSize: '13px', lineHeight: '1.5', color: '#1e2937', background: '#f8fafc', padding: '10px', borderRadius: '6px', borderLeft: `3px solid ${THEME.primary}` }}>
                      {truncatedActivity(log.activity) || 'No activity'}
                    </div>
                  </div>
                ))
              )}
            </div>

            {hasMore && (
              <div ref={observerTarget} className="text-center py-4 border-top bg-white">
                <div className="spinner-border spinner-border-sm" style={{ color: THEME.primary }} role="status" />
                <span className="ms-2 text-muted">Loading more logs...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedActivity && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }} onClick={() => setSelectedActivity(null)}>
          <div style={{ width: '100%', maxWidth: '720px', background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <div style={{  background: THEME.primary, color: THEME.onPrimary, padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: '15px' }}>Executed Query</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button onClick={copyToClipboard} style={{ background: 'transparent', border: 'none', color: THEME.onPrimary, padding: '4px' }} title="Copy Query" >
                  {isCopied ? <span style={{ color: '#4ade80', fontSize: '18px', fontWeight: 'bold' }}>✓✓</span> : <Copy size={18} />}
                </button>
                <button onClick={() => setSelectedActivity(null)} style={{ fontSize: '26px', background: 'none', border: 'none', color: THEME.onPrimary }} >
                  ×
                </button>
              </div>
            </div>

            <div style={{ padding: '18px', fontSize: '13.5px', lineHeight: '1.55', background: '#f8f9fa', maxHeight: '68vh', overflowY: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'Consolas, Monaco, monospace', borderBottom: '1px solid #eee' }}>
              {formatSQL(selectedActivity)}
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default QueryAnalyzerLogs;